import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, API, bg, setArea } from "@/lib/api";
import { stopImpersonation } from "@/components/ImpersonationBanner";
import { UserAvatar, roleLabel } from "@/components/UserAvatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Settings2, Mail, Sun, Moon, UserCog, LogOut, Bell, Smartphone, Repeat, Scissors, FlaskConical, Monitor } from "lucide-react";
import { InstallDialog, isStandalone } from "@/components/InstallApp";

export function basePathFor(role) {
  if (role === "admin") return "/admin";
  if (role === "instructor") return "/prof";
  return "/app";
}

// professora que também é aluna: troca de área recarregando a página (o servidor lê o cookie da área)
function switchArea(to) {
  setArea(to);
  window.location.assign(to === "aluno" ? "/app" : "/prof");
}

export function useUnreadMessages() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/messages/unread-count", bg).then((r) => alive && setCount(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("iep:messages-read", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("iep:messages-read", load); };
  }, []);
  return count;
}

// Suporte pelo WhatsApp (botão ao lado do sino, para alunas e professoras)
const SUPORTE_WHATSAPP = "5585988203236";
const WhatsIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 6.99 2.9 9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.29A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.48-8.41" />
  </svg>
);

const itemCls = "flex items-center gap-3 px-3 py-2.5 text-[15px] font-semibold cursor-pointer text-slate-700 dark:text-slate-200";
const iconCls = "h-5 w-5 text-[#1E9E2A]";

function useNotifications() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = () => api.get("/notifications", bg).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const readAll = () => { if (data.unread) api.post("/notifications/read-all").then(load).catch(() => {}); };
  return { ...data, readAll };
}

const fmtAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `há ${Math.max(1, Math.floor(d / 60))} min`;
  if (d < 86400) return `há ${Math.floor(d / 3600)} h`;
  return new Date(iso).toLocaleDateString("pt-BR");
};

export const UserMenu = () => {
  const { user, setUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const unread = useUnreadMessages();
  const notif = useNotifications();
  const [installOpen, setInstallOpen] = useState(false);
  const base = basePathFor(user?.role);

  const suporteMsg = encodeURIComponent(`Olá! Preciso de suporte na plataforma IEP.\n\n*Nome:* ${user?.name || ""}\n*E-mail:* ${user?.email || ""}\n*Perfil:* ${user?.role === "instructor" ? "Professora" : "Aluna"}\n\n*Minha dúvida:* `);
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      {/* ferramentas liberadas para esta pessoa, sempre à vista no topo */}
      {user?.role !== "admin" && user?.tricolab && (
        <a href="/tricolab" target="_blank" rel="noopener" title="Tricolab (abre em outra guia)" data-testid="header-tricolab"
          className="h-10 w-10 sm:w-auto grid place-items-center sm:flex sm:items-center sm:gap-1.5 sm:px-3 rounded-full text-white transition-opacity hover:opacity-90" style={{ background: "#0f1f1a" }}>
          <FlaskConical className="h-5 w-5 text-[#7fd1a6]" />
          <span className="hidden sm:inline text-sm font-semibold">Tricolab</span>
        </a>
      )}
      {user?.role !== "admin" && user?.salao && (
        <a href={`${API}/sso/salao`} target="_blank" rel="noopener" title="Meu Salão (abre em outra guia)" data-testid="header-salao"
          className="h-10 w-10 sm:w-auto grid place-items-center sm:flex sm:items-center sm:gap-1.5 sm:px-3 rounded-full border border-[#E3C48F] text-[#926B38] hover:bg-amber-50 transition-colors">
          <Scissors className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-semibold">Meu Salão</span>
        </a>
      )}
      {user?.role !== "admin" && (
        <a href={`https://wa.me/${SUPORTE_WHATSAPP}?text=${suporteMsg}`} target="_blank" rel="noopener noreferrer" title="Suporte"
          className="h-10 grid place-items-center rounded-full text-[#1E9E2A] hover:bg-green-50 transition-colors sm:px-3 sm:flex sm:items-center sm:gap-1.5 w-10 sm:w-auto" data-testid="header-whatsapp-support">
          <WhatsIcon className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-semibold">Suporte</span>
        </a>
      )}
      <DropdownMenu modal={false} onOpenChange={(o) => o && notif.readAll()}>
        <DropdownMenuTrigger asChild>
          <button className="relative h-10 w-10 grid place-items-center rounded-full text-[#1E9E2A] hover:bg-slate-100 transition-colors" title="Notificações" data-testid="header-messages-button">
            <Bell className="h-5 w-5" />
            {unread + notif.unread > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
                {unread + notif.unread > 99 ? "99+" : unread + notif.unread}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={10} className="w-[340px] p-0 rounded-xl overflow-hidden" data-testid="notifications-panel">
          <div className="px-4 py-3 font-semibold text-slate-800 border-b border-slate-100">Notificações</div>
          <div className="max-h-80 overflow-y-auto iep-scroll">
            {notif.items.length === 0 && <p className="px-4 py-5 text-sm text-slate-400">Nenhuma notificação.</p>}
            {notif.items.map((n) => (
              <DropdownMenuItem key={n.id} onSelect={() => n.link && navigate(n.link)} className="flex flex-col items-start gap-0.5 px-4 py-3 cursor-pointer">
                <span className={`text-sm ${n.read_at ? "text-slate-600" : "text-slate-900 font-semibold"}`}>{n.title}</span>
                <span className="text-xs text-slate-400">{n.body} · {fmtAgo(n.created_at)}</span>
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator className="m-0" />
          <DropdownMenuItem onSelect={() => navigate(`${base}/mensagens`)} className="px-4 py-3 cursor-pointer text-sm font-medium">
            <Mail className="h-4 w-4 mr-2 text-[#1E9E2A]" /> Mensagens
            {unread > 0 && <span className="ml-auto text-[11px] font-bold bg-red-500 text-white rounded-full px-2 py-0.5">{unread}</span>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E9E2A]" data-testid="user-menu-trigger">
            <UserAvatar user={user} size={40} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={10} className="w-[320px] p-0 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 p-4">
            <UserAvatar user={user} size={48} />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900 dark:text-white truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{roleLabel(user)}</div>
            </div>
            <button
              onClick={() => navigate(`${base}/perfil`)}
              className="shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              data-testid="user-menu-view-profile"
            >
              Ver perfil
            </button>
          </div>
          <DropdownMenuSeparator className="m-0" />
          <div className="p-1.5">
            {user?.impersonator && (
              <DropdownMenuItem className="flex items-center gap-3 px-3 py-2.5 text-[15px] font-bold cursor-pointer text-[#B45309] bg-amber-50 focus:bg-amber-100 focus:text-[#B45309]" onSelect={() => stopImpersonation(setUser, navigate)} data-testid="user-menu-stop-impersonation">
                <Repeat className="h-5 w-5" /> Voltar para o admin
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem className={itemCls} onSelect={() => navigate("/admin")} data-testid="user-menu-admin">
                <Settings2 className={iconCls} /> Administrar
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className={itemCls} onSelect={() => navigate(`${base}/mensagens`)} data-testid="user-menu-messages">
              <Mail className={iconCls} /> Mensagens
              {unread > 0 && <span className="ml-auto text-[11px] font-bold bg-red-500 text-white rounded-full px-2 py-0.5">{unread}</span>}
            </DropdownMenuItem>
            <DropdownMenuItem className={itemCls} onSelect={(e) => { e.preventDefault(); toggleTheme(); }} data-testid="user-menu-theme">
              {theme === "dark" ? <Sun className={iconCls} /> : <Moon className={iconCls} />}
              {theme === "dark" ? "Modo claro" : "Modo escuro"}
            </DropdownMenuItem>
            {user?.salao && (
              <DropdownMenuItem className={itemCls} onSelect={() => { window.location.href = `${API}/sso/salao`; }} data-testid="user-menu-salao">
                <Scissors className={iconCls} /> Meu Salão
              </DropdownMenuItem>
            )}
            {user?.tricolab && (
              <DropdownMenuItem className={itemCls} onSelect={() => { window.open("/tricolab", "_blank", "noopener"); }} data-testid="user-menu-tricolab">
                <FlaskConical className={iconCls} /> Tricolab
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className={itemCls} onSelect={() => navigate("/entrar-computador")} data-testid="user-menu-computador">
              <Monitor className={iconCls} /> Entrar no computador
            </DropdownMenuItem>
            <DropdownMenuItem className={itemCls} onSelect={() => navigate(`${base}/conta`)} data-testid="user-menu-account">
              <UserCog className={iconCls} /> Minha conta
            </DropdownMenuItem>
            {user?.dual && (
              <DropdownMenuItem className={itemCls} onSelect={() => switchArea(user.role === "student" ? "prof" : "aluno")} data-testid="user-menu-switch-area">
                <Repeat className={iconCls} /> {user.role === "student" ? "Ir para a área da professora" : "Ir para a área do aluno"}
              </DropdownMenuItem>
            )}
            {!isStandalone() && (
              <DropdownMenuItem className={itemCls} onSelect={() => setInstallOpen(true)} data-testid="user-menu-install">
                <Smartphone className={iconCls} /> Instalar app
              </DropdownMenuItem>
            )}
          </div>
          <DropdownMenuSeparator className="m-0" />
          <div className="p-1.5">
            <DropdownMenuItem
              className="flex items-center gap-3 px-3 py-2.5 text-[15px] font-semibold cursor-pointer text-red-500 focus:text-red-600"
              onSelect={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" /> Sair da conta
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <InstallDialog open={installOpen} onOpenChange={setInstallOpen} />
    </div>
  );
};
